import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBarTheme from './NavBar/NavBarTheme';
import StudentInterestForm from './Form/StudentInterestForm';
import HolidayForm from './Form/HolidayForm';
import IntegrationForm from './Form/IntegrationForm';
import { ClipLoader } from 'react-spinners';
import { server, logData } from '../api.js';
import { useAuth } from '../useAuth';

function ThemeStormer() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [themeSuggestionsShown, setThemeSuggestionsShown] = useState('false');
  const [suggestedThemes, setSuggestedThemes] = useState([]);
  const [customTheme, setCustomTheme] = useState('');
  const [selectedPathway, setSelectedPathway] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const navigate = useNavigate();

  const auth = useAuth();
  const rc = auth.getResearchCode();
  const suggestedThemesRef = useRef(null);

  useEffect(() => {
    logData(rc, 'THEMESTORMER_LOADED', {});
    const notUseThemeStormer = localStorage.getItem('notUseThemeStormer') === 'true';
    if (notUseThemeStormer) {
      logData(rc, 'THEMESTORMER_REDIRECTED_TO_PATHWAY_SELECTION', {});
      navigate('/pathwayselection');
    }
  }, [navigate, rc]);

  useEffect(() => {
    const pathway = localStorage.getItem('selectedPathway');
    if (pathway) {
      setSelectedPathway(pathway);
      logData(rc, 'THEMESTORMER_PATHWAY_SELECTED', { selectedPathway: pathway });

      // const enableThemeGeneration = localStorage.getItem('enableThemeGeneration');
      // if (enableThemeGeneration !== 'true') {
      //   const storedThemes = JSON.parse(localStorage.getItem('suggestedThemes'));
      //   if (storedThemes && storedThemes.length > 0) {
      //     setSuggestedThemes(storedThemes);
      //     setFormSubmitted(true);
      //   }
      // }
    }
  }, [rc]);

const handleFormSubmit = async (formData) => {
  if (isSubmitting) return;

  setIsSubmitting(true);
  setSuggestedThemes([]);
  localStorage.removeItem('suggestedThemes');

  const profileData =
    JSON.parse(localStorage.getItem('teacherProfile')) || {};

  const payload = {
    ...profileData,
    ...formData,
    selectedPathway,
  };

  try {
    logData(rc, 'THEMESTORMER_SUBMITTED', { themeData: payload });

    const response = await fetch(`${server}/generate_themes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (data.themes) {
      setSuggestedThemes(data.themes);
      localStorage.setItem(
        'suggestedThemes',
        JSON.stringify(data.themes)
      );
      localStorage.setItem('themeSuggestionsShown', 'true');
    } else {
      console.error('Failed to generate themes:', data.error);
    }
  } catch (error) {
    console.error('Error generating themes:', error);
  } finally {
    setIsSubmitting(false);
  }
};

  useEffect(() => {
    if (suggestedThemes.length > 0 && suggestedThemesRef.current) {
      suggestedThemesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [suggestedThemes]);

  const handleThemeSelect = (theme) => {
    logData(rc, 'THEMESTORMER_THEME_SELECTED', { selected: theme });
    localStorage.setItem('projectTheme', theme);
    localStorage.setItem('enableProjectIdeaGeneration', 'true');
    navigate('/projectstormer');
  };

  const handleThemeInput = (theme) => {
    logData(rc, 'THEMESTORMER_THEME_INPUT', { selected: theme });
    localStorage.setItem('projectTheme', theme);
    localStorage.setItem('enableProjectIdeaGeneration', 'true');
    navigate('/projectstormer');
  };

  const renderCustomThemeInput = () => (
    <div>
      <input
        type="text"
        className="form-control"
        value={customTheme}
        onChange={(e) => setCustomTheme(e.target.value)}
      />
      <div className="text-start">
        <button
          className="btn btn-primary mt-4"
          onClick={() => handleThemeInput(customTheme)}
          disabled={!customTheme.trim()}
        >
          Use My Theme
        </button>
      </div>
    </div>
  );

  const renderThemeForm = () => (
    <div className="container w-75 d-grid gap-2 mb-5">
     {selectedPathway === 'Academic Integration' && (
        <IntegrationForm onSubmit={handleFormSubmit} formSubmitted={formSubmitted} />
      )}
      {selectedPathway === 'Interests and Local Events' && (
        <StudentInterestForm onSubmit={handleFormSubmit} formSubmitted={formSubmitted} />
      )}
      {selectedPathway === 'Holidays and Traditions' && (
        <HolidayForm
          onSubmit={handleFormSubmit}
          isSubmitting={isSubmitting}
        />
      )}

      {isSubmitting && (
        <div className="text-center mt-4">
          <ClipLoader size={50} color={"#123abc"} loading={true} />
          <p>Generating themes...</p>
        </div>
      )}

      {suggestedThemes.length > 0 && (
        <div className="container mt-4" ref={suggestedThemesRef}>
          <h6 className="fw-bold mb-3">Click on <span className="text-primary fw-bold">ONE</span> of the themes below to use for your custom module.</h6>
          <p className="text-danger"><span className="fw-bold">ALERT!!!!!</span> AI can "make up" or generate false information.</p>
          <p className="text-danger">Be sure to <span className="fw-bold">FACT-CHECK</span> a theme before choosing it.</p>
          <ul className="list-group">
            {suggestedThemes.map((theme, index) => (
              <li
                key={index}
                className="list-group-item"
                onClick={() => handleThemeSelect(theme)}
              >
                {theme}
              </li>
            ))}
          </ul>

          <h6 className="fw-bold mt-4">Or if you now have a different theme idea you want to use, just enter it below.</h6>
          {renderCustomThemeInput()}
        </div>
      )}
    </div>
  );

  // Reset theme generation flag
  useEffect(() => {
    return () => {
      localStorage.removeItem('enableThemeGeneration');
    };
  }, []);

  return (
    <div className="row flex-nowrap">
      {/* Navbar */}
      <NavBarTheme />

      {/* Main Content */}
      <div className="col py-3">
        <div className="text-center">
          <img src={require("../assets/images/conjurorlogo.png")} className="w-25 mb-5" alt="logo"></img>
        </div>

        <div className="container w-75 d-grid gap-2 mb-5">
          {renderThemeForm()}
        </div>

      </div>
    </div>
  );
}

export default ThemeStormer;